"use client"

import { useState, useEffect } from "react"
import { SettingsSection } from "./settings-section"
import { SettingsToggle } from "./settings-toggle"
import { SettingsButtonGroup } from "./settings-button-group"
import { Sparkles, Sun, Zap, Brain } from "lucide-react"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"

export function AppPreferences() {
  const [aiModel, setAiModel] = useState<'haiku' | 'sonnet'>('haiku')

  // Load current AI model preference
  useEffect(() => {
    const user = AuthStore.getState().currentUser
    if (!user) return
    const students: any[] = StorageService.read('students')
    const student = students.find((s) => s.userId === user._id || s.email === user.email)
    if (student?.preferences?.aiModel) {
      setAiModel(student.preferences.aiModel)
    }
  }, [])

  const handleAiModelChange = (model: 'haiku' | 'sonnet') => {
    setAiModel(model)
    const user = AuthStore.getState().currentUser
    if (!user) return
    const students: any[] = StorageService.read('students')
    const idx = students.findIndex((s) => s.userId === user._id || s.email === user.email)
    if (idx === -1) return
    students[idx] = {
      ...students[idx],
      preferences: { ...students[idx].preferences, aiModel: model },
      updatedAt: new Date(),
    }
    StorageService.write('students', students)
  }

  const themeOptions = [
    { id: "light", icon: Sun, label: "Light Mode" },
  ]

  return (
    <SettingsSection title="App Preferences" icon={Sparkles}>
      <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
        <div className="flex flex-col">
          <p className="text-base font-bold text-slate-900">Theme Selection</p>
          <p className="text-sm font-normal text-slate-600">Choose a look that suits you.</p>
        </div>
        <SettingsButtonGroup options={themeOptions} defaultSelected="light" />
      </div>

      <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
        <div className="flex flex-col">
          <p className="text-base font-bold text-slate-900">Sound Effects</p>
          <p className="text-sm font-normal text-slate-600">Enable fun sounds for actions.</p>
        </div>
        <SettingsToggle id="toggle-sound" defaultChecked={true} />
      </div>

      {/* AI Companion Model */}
      <div className="p-4 rounded-lg bg-slate-50 flex flex-col gap-3">
        <div>
          <p className="text-base font-bold text-slate-900">AI Companion Model</p>
          <p className="text-sm font-normal text-slate-600">
            Choose how your AI companion responds. Fast replies vs deeper thinking.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleAiModelChange('haiku')}
            className={`flex flex-col gap-1 p-3 rounded-xl border-2 text-left transition-all ${
              aiModel === 'haiku'
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <span className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
              <Zap className="w-4 h-4 text-amber-500" />
              Fast (Haiku)
            </span>
            <span className="text-xs text-slate-500">Instant replies. Great for quick questions and study tips.</span>
          </button>
          <button
            onClick={() => handleAiModelChange('sonnet')}
            className={`flex flex-col gap-1 p-3 rounded-xl border-2 text-left transition-all ${
              aiModel === 'sonnet'
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <span className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
              <Brain className="w-4 h-4 text-indigo-500" />
              Smart (Sonnet)
            </span>
            <span className="text-xs text-slate-500">Deeper answers. Best for complex topics and career guidance.</span>
          </button>
        </div>
        <p className="text-[11px] text-slate-400">
          Currently using: <span className="font-semibold text-indigo-600">
            {aiModel === 'haiku' ? 'Claude Haiku — Fast' : 'Claude Sonnet — Smart'}
          </span>
        </p>
      </div>
    </SettingsSection>
  )
}
